import { message, Spin } from 'antd';
import axios from 'axios';
import { stringify } from 'qs';
import React, { useEffect, useState } from 'react';
import { genCookieName, TDS_TOKEN, TDS_USER_ID, TDS_USER_NAME } from '@/config/hooks/user/utils';
import { API_PREFIX, BusiCode } from '@/infrastructure/axios/code';
import { ENV } from '@/infrastructure/env-config';
import { LoginType } from '@/service/tencent-cloud';
import { setCookie } from '@raft/utils';

// 大疆 SSO client_id（authorize 跳转用，不敏感；client_secret 在后端 LoginAuth 配置用于换 token）
const DJI_CLIENT_ID = ENV.djiClientId || '200aa49bc18147b8aac0e818dd1ee8d6';

// 大疆 SSO OAuth endpoints
const DJI_SSO_BASE = ENV.isProd ? 'https://gcac.djicorp.com' : 'https://gcac-test.djicorp.com';

const REDIRECT_URI = ENV.isProd ? 'https://bugly.djiits.com/login/dji' : 'https://buglydji.testsite.woa.com/login/dji';

const getParamFromUrl = (key: string): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) || '';
};

/**
 * 大疆 SSO 登录页（OAuth 2.0 授权码模式）
 *
 * 流程：
 * 1. 用户未登录 → dji.ts logout 跳 /login/dji?url=<原URL>
 * 2. 进入页面无 code → 自动跳大疆 oauth/authorize，state 携带原URL（无需用户点击）
 * 3. 大疆回调 /login/dji?code=xxx&state=<原URL>
 * 4. 取 code 调 /v5/api/login(platform=dji_oauth) → 后端用 code+secret 换 token
 * 5. 写 cookie → 跳回原URL（state）
 *
 * 参考：apps/shiply/framework/src/pages/login/dji/index.tsx
 */
const DjiLoginPage: React.FC = () => {
  const [loading, setLoading] = useState(true);

  // 1. 跳转大疆 SSO 授权页，state 携带原URL
  const requestAuth = () => {
    const params = {
      client_id: DJI_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      response_type: 'code',
      state: getParamFromUrl('url'),
    };
    window.location.href = `${DJI_SSO_BASE}/oauth/authorize?${stringify(params)}`;
  };

  // 2. 大疆回调带 code → 调 /v5/api/login 换 buglyToken → 写 cookie → 跳回原URL
  const onAuthCallback = async (code: string, state: string) => {
    setLoading(true);
    try {
      const res = await axios.post(`/${API_PREFIX}/api/login`, {
        baseParam: { platform: LoginType.DjiOauth },
        djiOauth: { code, redirectUri: REDIRECT_URI },
      });
      const { data } = res;
      if (data?.baseRsp?.code === BusiCode.Ok) {
        const expiresInDay = 30;
        // 先写 cookie 再跳转，确保跳回原页时登录态已生效（checkLogin 能读到 tds_token）
        setCookie(genCookieName(TDS_TOKEN), data?.buglyToken, expiresInDay, '/');
        setCookie(genCookieName(TDS_USER_ID), data?.userInfo?.buglyUserId, expiresInDay, '/');
        setCookie(genCookieName(TDS_USER_NAME), data?.userInfo?.buglyUserName, expiresInDay, '/');
        window.location.href = decodeURIComponent(state) || window.location.origin;
      } else {
        message.error(`登录失败：[${data?.baseRsp?.code}]${data?.baseRsp?.msg || ''}`);
      }
    } catch (e) {
      message.error('登录请求异常，请稍后重试');
      // eslint-disable-next-line no-console
      console.error('[dji-login] 请求异常', e);
    }
    setLoading(false);
  };

  // 3. 进入页面时：带 code（大疆回调）→ 换 token；否则自动跳大疆授权页
  useEffect(() => {
    const code = getParamFromUrl('code');
    const state = getParamFromUrl('state');
    if (code) {
      onAuthCallback(code, state);
    } else {
      requestAuth();
    }
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Spin size="large" spinning={loading} tip="登录中..." />
    </div>
  );
};

export default DjiLoginPage;
